import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { STAGES, STAGE_COLORS } from "@/lib/i18n";
import { Download } from "lucide-react";

export default function Reports() {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [repKpis, setRepKpis] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/dashboard/rep-kpis").then((r) => setRepKpis(r.data)).catch(() => {});
  }, []);

  const exportCsv = () => {
    if (!stats) return;
    const lines = ["Metric,Value"];
    Object.entries(stats.totals).forEach(([k, v]) => lines.push(`${k},${v}`));
    lines.push(`conversion_rate,${stats.conversion_rate}%`);
    lines.push(`pipeline_value,${stats.total_pipeline_value}`);
    lines.push(`won_value,${stats.won_value}`);
    lines.push("");
    lines.push("Stage,Count");
    Object.entries(stats.stage_counts).forEach(([k, v]) => lines.push(`${t(`stage.${k}`)},${v}`));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "report.csv"; a.click(); URL.revokeObjectURL(url);
  };

  if (!stats) return <div>{t("common.loading")}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{t("nav.reports")}</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">Reports</h1>
        </div>
        <button onClick={exportCsv} data-testid="export-csv-button" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-md text-sm font-medium hover:bg-slate-50">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Funnel</h2>
          <div className="space-y-2.5">
            {STAGES.map((s) => {
              const c = stats.stage_counts[s] || 0;
              const max = Math.max(...Object.values(stats.stage_counts), 1);
              return (
                <div key={s} className="flex items-center gap-3 text-sm">
                  <div className="w-44 text-slate-700">{t(`stage.${s}`)}</div>
                  <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden">
                    <div style={{ width: `${(c / max) * 100}%`, backgroundColor: STAGE_COLORS[s] }} className="h-full" />
                  </div>
                  <div className="w-10 text-right font-semibold">{c}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">By country</h2>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200"><th className="py-2 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Country</th><th className="py-2 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Deals</th><th className="py-2 text-right text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Value</th></tr></thead>
            <tbody>
              {Object.entries(stats.country_performance).length === 0 ? (
                <tr><td colSpan={3} className="py-6 text-center text-slate-400">{t("common.no_data")}</td></tr>
              ) : Object.entries(stats.country_performance).map(([c, v]) => (
                <tr key={c} className="border-b border-slate-100">
                  <td className="py-3 font-medium">{c}</td>
                  <td className="py-3">{v.deals}</td>
                  <td className="py-3 text-right font-semibold">€{Math.round(v.value).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Sales rep KPIs</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {["Rep", "Calls", "Duration", "Opps", "Won", "Lost", "Conv. %"].map((h) => (
                  <th key={h} className="py-3 px-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {repKpis.length === 0 ? (
                <tr><td colSpan={7} className="py-8 text-center text-slate-400">{t("common.no_data")}</td></tr>
              ) : repKpis.map((r) => (
                <tr key={r.rep.id} className="border-b border-slate-100">
                  <td className="py-3 px-4 font-medium">{r.rep.name}</td>
                  <td className="py-3 px-4">{r.calls}</td>
                  <td className="py-3 px-4">{r.call_duration}</td>
                  <td className="py-3 px-4">{r.opportunities}</td>
                  <td className="py-3 px-4 text-emerald-700 font-semibold">{r.deals_won}</td>
                  <td className="py-3 px-4 text-red-600">{r.deals_lost}</td>
                  <td className="py-3 px-4">{r.conversion_rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
